import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LocationAutocomplete } from "@/components/LocationAutoComplete";
import { AddressType, UserAddress } from "@/api/SolarCloudApi";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { addressSchema } from "@/queries/user/useUpdateUserProfile";
import { useCountries } from "@/hooks/Countries";
import { FlagImage } from "react-international-phone";

export type AddressFormData = z.infer<typeof addressSchema>;

/**
 * AddressForm Component
 *
 * A reusable component that combines AddressSearch with form fields for address input.
 * Uses the existing addressSchema from useUpdateUserProfile for validation.
 *
 * @example
 * ```tsx
 * <AddressForm
 *   form={form}
 *   name="address"
 *   placeholder="Search for your address"
 *   showSearch={true}
 * />
 * ```
 *
 * This will create form fields for:
 * - Address search (optional)
 * - Country
 * - Street Address
 * - City, State, Postcode (in a flex layout)
 */
interface AddressFormProps {
  form: UseFormReturn<any>;
  name: string;
  placeholder?: string;
  showSearch?: boolean;
  className?: string;
}

export function AddressForm({
  form,
  name,
  placeholder = "Search for your address",
  showSearch = true,
  className = "",
}: AddressFormProps) {
  const [address, setAddress] = useState<UserAddress>();
  const { countries } = useCountries();

  const currentAddress = form.watch(name);

  useEffect(() => {
    if (!address && currentAddress) {
      setAddress(currentAddress as UserAddress);
    }
  }, [address, currentAddress]);

  const handleAddressSelected = (selectedAddress: UserAddress) => {
    setAddress(selectedAddress);
    form.setValue(
      `${name}`,
      {
        country: selectedAddress.country || "",
        line1: selectedAddress.line1 || "",
        line2: selectedAddress.line2 || "",
        line3: selectedAddress.line3 || "",
        line4: selectedAddress.line4 || "",
        city: selectedAddress.city || "",
        state: selectedAddress.state || "",
        postcode: selectedAddress.postcode || "",
        type: selectedAddress.type || AddressType.HOME,
        uuid: selectedAddress.uuid || "",
      },
      {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      }
    );
  };

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {showSearch && (
        <FormField
          control={form.control}
          name={`${name}.line1`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <LocationAutocomplete
                  address={address}
                  setAddress={handleAddressSelected}
                  options={{ onlyAvailable: true, allowOverride: true }}
                  placeholder={placeholder}
                  countries={countries}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name={`${name}.country`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Country</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full bg-brand-neutral-800">
                  <SelectValue placeholder="Select a country">
                    {field.value && (() => {
                      const countryCode = countries
                        ?.find((c) => c.name === field.value)
                        ?.code?.toLowerCase();
                      return (
                        <div className="flex items-center gap-2">
                          {countryCode && (
                            <FlagImage
                              iso2={countryCode as any}
                              style={{ display: "inline-block" }}
                            />
                          )}
                          <span>{field.value}</span>
                        </div>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {countries?.map((country) => {
                    const countryCode = country.code?.toLowerCase();
                    return (
                      <SelectItem key={country.code} value={country.name || ""}>
                        <div className="flex items-center gap-2">
                          {countryCode && (
                            <FlagImage
                              iso2={countryCode as any}
                              style={{ display: "inline-block" }}
                            />
                          )}
                          <span>{country.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex gap-4 items-start">
        <FormField
          control={form.control}
          name={`${name}.city`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input
                  placeholder=""
                  {...field}
                  className="bg-brand-neutral-800"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${name}.state`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <Input
                  placeholder=""
                  {...field}
                  className="bg-brand-neutral-800"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${name}.postcode`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Postcode</FormLabel>
              <FormControl>
                <Input
                  placeholder=""
                  {...field}
                  className="bg-brand-neutral-800"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
